import { ChatMessageTypeEnum } from 'lib-trend-core';

// Attachment rules for the internal chat (SPEC-2026-07-internal-chat, T-33).
// CLIENT-SIDE MIRROR of trendmulti-apps/apps/trendmulti-chat/src/util/
// chat-content.util.ts — limits and the blocked-extension list MUST stay
// byte-identical to the backend constants (RNF-06).

export const CHAT_MAX_ATTACHMENTS_PER_MESSAGE = 10;
export const CHAT_MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

export const CHAT_BLOCKED_FILE_EXTENSIONS: string[] =
  ['.exe', '.bat', '.sh', '.cmd', '.msi', '.com', '.scr'];

/** Attachment descriptor riding the MESSAGE_SEND payload (plan §8). */
export interface ChatSendAttachment {
  fileUrl: string;
  contentType: string;
  fileName: string;
  fileSize: number;
}

/** True when the file name carries one of the blocked executable extensions. */
export function isBlockedChatFileExtension(fileName: string): boolean {
  if (!fileName) return false;
  const normalized = fileName.trim().toLowerCase();
  return CHAT_BLOCKED_FILE_EXTENSIONS.some((extension) => normalized.endsWith(extension));
}

/**
 * Resolves the ChatMessage type from an attachment MIME type — the exact
 * mapping the backend applies when splitting the send into one message per
 * attachment (chatMessageTypeFromContentType).
 */
export function chatMessageTypeFromContentType(contentType: string | undefined): ChatMessageTypeEnum {
  const normalized = (contentType || '').toLowerCase();
  if (normalized.startsWith('image/')) return ChatMessageTypeEnum.IMAGE;
  if (normalized.startsWith('audio/')) return ChatMessageTypeEnum.AUDIO;
  if (normalized.startsWith('video/')) return ChatMessageTypeEnum.VIDEO;
  return ChatMessageTypeEnum.DOCUMENT;
}

/** Human-readable file size (technical units — intentionally untranslated). */
export function formatChatFileSize(bytes: number | undefined): string {
  const size = Number(bytes) || 0;
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
}

/** Remix icon for a non-image attachment chip / document card. */
export function chatFileIcon(contentType: string | undefined, fileName: string | undefined): string {
  const mime = (contentType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();

  if (mime.startsWith('image/')) return 'ri-image-line';
  if (mime.startsWith('video/')) return 'ri-film-line';
  if (mime.startsWith('audio/')) return 'ri-music-2-line';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'ri-file-pdf-line';
  if (/\.(zip|rar|7z|tar|gz)$/.test(name)) return 'ri-file-zip-line';
  if (/\.(xls|xlsx|csv)$/.test(name) || mime.includes('spreadsheet')) return 'ri-file-excel-2-line';
  if (/\.(doc|docx)$/.test(name) || mime.includes('wordprocessing')) return 'ri-file-word-2-line';
  if (/\.(ppt|pptx)$/.test(name) || mime.includes('presentation')) return 'ri-file-ppt-2-line';
  return 'ri-file-text-line';
}
